import React, { useState, useEffect } from 'react';
import { ChartBarIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import DAPTable from '../DAPTable/DAPTable';
// Import from our debug version of the API service
import { getDQRColumns, getDQRRows, getDQRStats } from '../../services/api';
import './DQRReport.css'; // Import the CSS file

// Define interfaces locally if they're not available in types
interface DAPTableColumn {
  key: string;
  label: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render?: (value: any, row?: any, onChange?: (val: any) => void) => React.ReactNode;
}

interface DAPTableRow {
  [key: string]: any;
}

interface DQRReportProps {
  isActive?: boolean;
}

const DQRReport: React.FC<DQRReportProps> = ({ isActive = true }) => {
  // State for columns, data, and selected column
  const [columns, setColumns] = useState<string[]>([]);
  const [data, setData] = useState<DAPTableRow[]>([]);
  const [selectedColumn, setSelectedColumn] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [error, setError] = useState<string | null>(null);

  // Fetch columns on component mount
  useEffect(() => {
    if (isActive) {
      fetchColumns();
    }
  }, [isActive]);

  // Fetch data when selected column changes or pagination/sorting changes
  useEffect(() => {
    if (isActive && (columns.length > 0 || selectedColumn === 'all')) {
      fetchData();
    }
  }, [isActive, selectedColumn, page, pageSize, sortColumn, sortDirection]);

  // Fetch columns from the API
  const fetchColumns = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getDQRColumns();
      setColumns(response.columns);
      setSelectedColumn(''); // Default to empty string for placeholder
    } catch (error) {
      console.error('Error fetching DQR columns:', error);
      setError('Failed to fetch columns. Please check the console for details.');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data from the API
  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Prepare filters based on selected column
      const filters: Record<string,string> = {};
      if (selectedColumn !== 'all' && selectedColumn !== '') {
        console.log('Setting filter for column:', selectedColumn);
        // Add the selected column as a filter
        filters[selectedColumn] = 'true'; // Filter for rows where this column is true
        console.log('Filters after setting:', filters);
      }

      console.log('Calling getDQRRows with filters:', filters);
      const response = await getDQRRows(page, pageSize, filters);
      setData(response.rows);
      setTotalRows(response.metadata.total_rows);
      setTotalPages(response.metadata.total_pages);
    } catch (error) {
      console.error('Error fetching DQR data:', error);
      setError('Failed to fetch data. Please check the console for details.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle column selection change
  const handleColumnChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedColumn(e.target.value);
    setPage(1); // Reset to first page when column selection changes
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  // Handle page size change
  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(parseInt(e.target.value, 10));
    setPage(1); // Reset to first page when page size changes
  };

  // Handle sorting
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Prepare table columns
  const getTableColumns = (): DAPTableColumn[] => {
    if (!data.length) return [];

    // If 'all' is selected, use all columns
    if (selectedColumn === 'all') {
      return Object.keys(data[0]).map(key => ({
        key,
        label: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        align: typeof data[0][key] === 'boolean' ? 'center' : 'left',
        width: '200px', // Set a fixed width for all columns
        render: (value: any) => {
          if (typeof value === 'boolean') {
            return (
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {value ? 'Yes' : 'No'}
              </span>
            );
          }
          return value;
        }
      }));
    }

    // If specific column is selected, show row_index and that column
    return [
      {
        key: 'row_index',
        label: 'Row Index',
        align: 'left'
      },
      {
        key: selectedColumn,
        label: selectedColumn.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        align: 'center',
        render: (value: any) => {
          if (typeof value === 'boolean') {
            return (
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {value ? 'Yes' : 'No'}
              </span>
            );
          }
          return value;
        }
      }
    ];
  };

  // Filter data based on selected column
  const getFilteredData = (): DAPTableRow[] => {
    if (selectedColumn === 'all' || !data.length) {
      return data;
    }

    // For specific column, only show rows where that column is true (for boolean columns)
    if (typeof data[0][selectedColumn] === 'boolean') {
      return data.filter(row => row[selectedColumn] === true);
    }

    return data;
  };

  // Pagination controls
  const renderPagination = () => {
    return (
      <div className="flex flex-wrap items-center justify-between mt-4 gap-4">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-700">Rows per page:</span>
          <select
            value={pageSize}
            onChange={handlePageSizeChange}
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
            className={`px-3 py-1 rounded text-sm ${
              page === 1 ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            Previous
          </button>

          <span className="text-sm text-gray-700">
            Page {page} of {totalPages}
          </span>

          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
            className={`px-3 py-1 rounded text-sm ${
              page === totalPages ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="flex-col flex w-200">
       {/*Column selection dropdown */}
       <div className="mb-6 px-4">
        <label htmlFor="column-select" className="block text-sm font-medium text-gray-700 mb-2">
          Select Column to Filter
        </label>
        <div className="relative w-64">
          <select
            id="column-select"
            value={selectedColumn}
            onChange={handleColumnChange}
            className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md appearance-none"
            disabled={isLoading || columns.length === 0}
          >
            <option value="" disabled>Select Column</option>
            <option value="all">All Columns</option>
            {columns.map((column) => (
              <option key={column} value={column}>
                {column.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
            <ChevronDownIcon className="h-4 w-4" />
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6 mx-4">
          <div className="flex">
            <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 10.939l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.25 8.27a.75.75 0 01-.02-1.06z"
                    clipRule="evenodd"
                  />
                </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Data table */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <>
          {/* This is the key change - a flex-based layout with overflow handling */}
          {/*}<div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex-1 overflow-auto">
            <div className="flex-1 overflow-auto">*/}
              <DAPTable
                columns={getTableColumns()}
                data={getFilteredData()}
                title={`DQR Report ${selectedColumn !== 'all' && selectedColumn !== '' ? `- ${selectedColumn}` : ''}`}
                emptyMessage="No data available for the selected column."
                striped={true}
                hoverable={true}
                showSaveButton={false}
                showValidateButton={false}
              />
            {/*  </div>
            }</div>
          </div>*/}

          <div className="px-4 mt-4">
            {renderPagination()}
          </div>
        </>
      )}
     {/*</div>*/}
    </div>
  );
};

export default DQRReport;
